/**
 * GCIP Login Card
 *
 * Hosts the full Google Identity Platform sign-in flow in-app:
 *   email → (resolve agency GCIP tenant) → password → TOTP enroll/challenge.
 *
 * MFA (TOTP) is mandatory per PE-21. A first-time user with no enrolled factor
 * is routed through enrollment; thereafter every sign-in completes a TOTP
 * challenge. On success the Firebase ID token is exchanged for the __session
 * cookie via POST /api/auth/session and the user is routed to `redirect`.
 */

'use client';

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { getAuthClient, type AuthUser } from '@/lib/auth-client';
import {
  GcipAuthError,
  MFA_CHALLENGE_REQUIRED,
  MFA_ENROLLMENT_REQUIRED,
  MFA_NO_CHALLENGE,
  MFA_NO_ENROLLMENT,
  MFA_NO_USER,
  type GCIPAuthClient,
} from '@/lib/auth-client-gcip';
import { FoltriggLogo } from '@/components/FoltriggLogo';
import { AuthBackground } from '@/components/AuthBackground';
import { getPasswordRequirementsText } from '@/lib/validation-utils';
import { markFreshLogin } from '@/lib/login-tenant-handoff';

type Step = 'email' | 'select-tenant' | 'password' | 'enroll' | 'challenge';

interface PresetTenant {
  gcipTenantId: string | null;
  tenantName?: string;
}

interface TenantChoice {
  gcipTenantId: string | null;
  tenantName: string;
  slug: string;
}

interface Props {
  redirect: string;
  presetTenant?: PresetTenant;
  /** Shown in the alert before any interaction, e.g. why the session ended. */
  initialError?: string;
}

function gcipClient(): GCIPAuthClient {
  return getAuthClient() as unknown as GCIPAuthClient;
}

// gh #1007: the transient MFA resolver / enrollment secret is in-memory on the
// auth-client singleton. It is lost on a full page reload, or after a
// successful resolveSignIn whose follow-up finishAuthenticated then throws
// (leaving the code screen up with a now-null resolver). Re-submitting a code
// in that state throws NO_CHALLENGE ("No multi-factor challenge in progress").
// Detect that class so the UI can restart sign-in instead of dead-ending.
function isLostSignInStateError(err: unknown): boolean {
  return (
    err instanceof GcipAuthError &&
    (err.code === MFA_NO_CHALLENGE ||
      err.code === MFA_NO_ENROLLMENT ||
      err.code === MFA_NO_USER)
  );
}

// The server-side GCIP password policy runs with forceUpgradeOnSignin, so a
// password set before the policy existed is rejected at sign-in. The user
// cannot sign in to reach the Settings change-password form, so point them at
// the reset flow.
export const PASSWORD_UPGRADE_REQUIRED_MESSAGE = `Your password no longer meets the security requirements. ${getPasswordRequirementsText()}.`;

function friendlyError(err: unknown): string {
  const code = (err as { code?: string })?.code;
  switch (code) {
    case 'auth/password-does-not-meet-requirements':
      return PASSWORD_UPGRADE_REQUIRED_MESSAGE;
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/invalid-verification-code':
    case 'auth/invalid-verification-id':
      return 'The code is invalid. Check the authenticator app and try again. For additional help, contact support';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/user-disabled':
      return 'This account is disabled. Contact your administrator.';
    case 'auth/firebase-app-check-token-is-invalid':
      return 'Security verification failed. If you are using a privacy-focused browser (like DuckDuckGo or Brave) or an ad-blocker, please disable tracking protection for this site or try a different browser.';
    default:
      return err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
  }
}

export function GcipLoginCard({ redirect, presetTenant, initialError }: Props) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrollInfo, setEnrollInfo] = useState<{ secretKey: string; qrCodeUrl: string } | null>(null);
  const [tenantChoices, setTenantChoices] = useState<TenantChoice[]>([]);
  const [selectedTenantName, setSelectedTenantName] = useState<string | null>(null);
  const [selectedGcipTenantId, setSelectedGcipTenantId] = useState<string | null>(
    presetTenant?.gcipTenantId ?? null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError ?? '');

  // Bind the agency GCIP tenant up front when arriving via a slug URL. The
  // selected-tenant state is seeded lazily from the prop above; this effect only
  // pushes the binding into the auth client (external side effect).
  useEffect(() => {
    if (presetTenant) {
      gcipClient().setTenant(presetTenant.gcipTenantId);
    }
  }, [presetTenant]);

  async function finishAuthenticated(_user: AuthUser) {
    const client = getAuthClient();
    const token = await client.getIdToken(true);
    if (!token) throw new Error('Could not obtain a session token.');
    const res = await fetch('/api/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) throw new Error('Failed to establish session.');
    // Tell TenantProvider that the load about to happen follows a sign-in, so
    // the agency chosen above beats any tenant a previous session left in
    // localStorage (gh #872 / #1165). Consumed once, on the next mount.
    markFreshLogin(gcipClient().getTenant());
    queryClient.clear();
    // gh #1758: a FULL document load, not router.push(). A client-side
    // transition mounts the destination against the AuthProvider instance that
    // is still mid-resolution from this sign-in's token events, so the
    // destination can observe { loading:false, user:null } and render its
    // unauthenticated dead-end — for which the only escape was the dashboard's
    // self-heal reload (the reported 10s hang). A full load re-bootstraps auth
    // from the __session cookie we just set + Firebase persistence, which is
    // the path that demonstrably works (it is what the self-heal reload does),
    // and it costs ~1s. `redirect` is sanitized to a same-origin absolute path
    // by sanitizeRedirect() in app/login/page.tsx (issue #822), so this cannot
    // be steered off-origin.
    window.location.href = redirect;
  }

  // gh #1007: return the wizard to the password step so signIn() re-arms a
  // fresh MFA resolver. Used when the current resolver/enrollment secret is
  // gone or was already spent — never leave the user re-submitting a code
  // against a null resolver. The password stays in state so a single click
  // re-drives the challenge.
  function restartSignIn(message: string) {
    setCode('');
    setEnrollInfo(null);
    setStep('password');
    setError(message);
  }

  // Email-first tenant resolution: the server returns the tenant(s) the email
  // is provisioned in. One match binds immediately; many present a picker; none
  // binds null (sign-in then fails uniformly at the password step). Always a
  // uniform 200 shape so an unknown email is not distinguishable (PE-22).
  async function resolveTenantByEmail(addr: string): Promise<Step> {
    if (presetTenant) {
      gcipClient().setTenant(presetTenant.gcipTenantId);
      setSelectedGcipTenantId(presetTenant.gcipTenantId);
      return 'password';
    }
    if (!addr.includes('@')) throw new Error('Enter a valid email address.');
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
    const res = await fetch(`${apiUrl}/api/tenants/by-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: addr }),
    });
    if (res.status === 429) {
      throw new Error('Too many attempts. Please wait a moment and try again.');
    }
    const data = res.ok ? await res.json() : { tenants: [] };
    const choices: TenantChoice[] = (data.tenants ?? []).map(
      (t: { gcip_tenant_id: string | null; tenant_name: string; slug: string }) => ({
        gcipTenantId: t.gcip_tenant_id,
        tenantName: t.tenant_name,
        slug: t.slug,
      }),
    );
    if (choices.length > 1) {
      setTenantChoices(choices);
      return 'select-tenant';
    }
    // Zero matches → null tenant (uniform failure at password). One match → bind it.
    gcipClient().setTenant(choices[0]?.gcipTenantId ?? null);
    setSelectedTenantName(choices[0]?.tenantName ?? null);
    setSelectedGcipTenantId(choices[0]?.gcipTenantId ?? null);
    return 'password';
  }

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const next = await resolveTenantByEmail(email.trim());
      setStep(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resolve organization.');
    } finally {
      setBusy(false);
    }
  }

  function onSelectTenant(choice: TenantChoice) {
    gcipClient().setTenant(choice.gcipTenantId);
    setSelectedTenantName(choice.tenantName);
    setSelectedGcipTenantId(choice.gcipTenantId);
    setError('');
    setStep('password');
  }

  // Carry the email and currently-bound org into the reset flow so the
  // forgot-password page can prefill the email and highlight the same org
  // (the user still confirms, but can click straight through).
  const forgotPasswordHref = `/forgot-password?${new URLSearchParams({
    ...(email.trim() ? { email: email.trim() } : {}),
    ...(selectedGcipTenantId ? { tenant: selectedGcipTenantId } : {}),
  }).toString()}`;

  async function onSubmitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const user = await getAuthClient().signIn(email.trim(), password);
      await finishAuthenticated(user);
    } catch (err) {
      if (err instanceof GcipAuthError && err.code === MFA_CHALLENGE_REQUIRED) {
        setCode('');
        setStep('challenge');
      } else if (err instanceof GcipAuthError && err.code === MFA_ENROLLMENT_REQUIRED) {
        try {
          const info = await gcipClient().startTotpEnrollment();
          setEnrollInfo(info);
          setCode('');
          setStep('enroll');
        } catch (enrollErr) {
          setError(friendlyError(enrollErr));
        }
      } else {
        setError(friendlyError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitEnroll(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);

    // Phase 1: finalize enrollment. A lost enrollment secret (NO_ENROLLMENT /
    // NO_USER — e.g. a full reload wiped the singleton) is a dead-end: restart
    // sign-in. A bad code surfaces as a normal retryable error on this step.
    let user: AuthUser;
    try {
      // enroll finalizes server-side and upgrades the token to second-factor;
      // the user is fully authenticated — no second challenge needed.
      user = await gcipClient().completeTotpEnrollment(code.trim());
    } catch (err) {
      if (isLostSignInStateError(err)) {
        restartSignIn('Your enrollment session expired. Please sign in again.');
      } else {
        setError(friendlyError(err));
      }
      setBusy(false);
      return;
    }

    // Phase 2: the factor is now enrolled server-side. If finishing the session
    // fails, a fresh sign-in will correctly land on the challenge step — so
    // restart rather than stranding the user on the (now-complete) enroll form.
    toast.success('Authenticator enrolled.');
    try {
      await finishAuthenticated(user);
    } catch {
      restartSignIn(
        'Your authenticator is enrolled, but we could not finish signing you in. Please sign in again.'
      );
      setBusy(false);
    }
  }

  async function onSubmitChallenge(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);

    // Phase 1: consume the single-use MFA resolver. A wrong code leaves the
    // resolver intact (Firebase rejects the assertion) so the user retries here.
    // A lost resolver (NO_CHALLENGE) is a dead-end — restart sign-in so signIn()
    // arms a fresh resolver instead of re-submitting against null (gh #1007).
    let user: AuthUser;
    try {
      user = await gcipClient().resolveTotpChallenge(code.trim());
    } catch (err) {
      if (isLostSignInStateError(err)) {
        restartSignIn('Your verification session expired. Please sign in again.');
      } else {
        setError(friendlyError(err));
      }
      setBusy(false);
      return;
    }

    // Phase 2: the resolver is now spent. A failure here must NOT leave the user
    // on a challenge step backed by a null resolver — restart sign-in.
    try {
      await finishAuthenticated(user);
    } catch {
      restartSignIn(
        'We verified your code but could not finish signing you in. Please sign in again.'
      );
      setBusy(false);
    }
  }

  const heading =
    step === 'email' || step === 'password'
      ? `Sign in to ${presetTenant?.tenantName ?? selectedTenantName ?? 'Foltrigg'}`
      : step === 'select-tenant'
        ? 'Select your organization'
        : step === 'enroll'
          ? 'Set up two-factor authentication'
          : 'Two-factor verification';

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      <AuthBackground />
      <div className="relative z-10 w-[416px] max-w-[calc(100vw-2rem)]">
        <div className="flex flex-col items-center gap-8 overflow-hidden rounded-2xl bg-white/60 px-12 py-14 shadow-2xl backdrop-blur-md">
          <FoltriggLogo variant="wordmark" className="h-10 w-auto" />

          <div className="flex flex-col gap-2 text-center text-[#0e0f23]">
            <h2 className="text-2xl font-medium leading-[30px] tracking-[-0.1px]">{heading}</h2>
            <p className="text-sm leading-relaxed text-[#0e0f23]/70">Legal case management platform</p>
          </div>

          <div className="flex w-full flex-col gap-4">
            {/* CJIS §5.8 / AC-8 System Use Notification. */}
            <div role="region" aria-label="System use notification" className="w-full rounded-lg bg-white p-5">
              <p className="text-center text-xs leading-relaxed text-[#0e0f23]">
                <span className="font-semibold text-[#e54d2e]">WARNING:</span>{' '}
                This is a restricted information system intended for authorized users only. System
                usage may be monitored, recorded, and subject to audit. Unauthorized or improper use
                is prohibited and subject to criminal and civil penalties. Use of this system
                constitutes consent to monitoring and auditing in accordance with the FBI CJIS
                Security Policy.
              </p>
            </div>

            {error && (
              <div role="alert" aria-live="assertive" className="rounded-lg border border-[#e54d2e]/30 bg-[#e54d2e]/10 p-3 text-center text-sm text-[#e54d2e]">
                {error === 'The code is invalid. Check the authenticator app and try again. For additional help, contact support' ? (
                  <>
                    The code is invalid. Check the authenticator app and try again.<br />
                    For additional help, <a href="mailto:support@foltrigg.ai?subject=Login%20issue" className="underline">contact support</a>
                  </>
                ) : error === PASSWORD_UPGRADE_REQUIRED_MESSAGE ? (
                  <>
                    {PASSWORD_UPGRADE_REQUIRED_MESSAGE}{' '}
                    <Link href={forgotPasswordHref} className="font-medium underline">
                      Reset your password
                    </Link>
                  </>
                ) : (
                  error
                )}
              </div>
            )}

            {step === 'email' && (
              <form onSubmit={onSubmitEmail} className="flex w-full flex-col gap-4">
                <input
                  type="email"
                  id="username"
                  name="username"
                  required
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@agency.gov"
                  className="h-10 w-full rounded-md border border-[#0e0f23]/15 bg-white px-3 text-sm text-[#0e0f23] focus:outline-none focus:ring-2 focus:ring-[#20556f]/50"
                />
                <SubmitButton busy={busy} label="Continue" />
              </form>
            )}

            {step === 'select-tenant' && (
              <div className="flex w-full flex-col gap-4">
                <p className="text-sm leading-relaxed text-[#0e0f23]/80">
                  Your account belongs to more than one organization. Choose which one to sign in to.
                </p>
                <div className="flex w-full flex-col gap-2">
                  {tenantChoices.map((choice) => (
                    <button
                      key={choice.slug}
                      type="button"
                      onClick={() => onSelectTenant(choice)}
                      className="flex h-10 w-full items-center justify-between rounded-md border border-[#0e0f23]/15 bg-white px-3 text-sm font-medium text-[#0e0f23] transition-colors hover:border-[#20556f]/50 hover:bg-[#20556f]/5 focus:outline-none focus:ring-2 focus:ring-[#20556f]/50"
                    >
                      <span>{choice.tenantName}</span>
                      <span aria-hidden="true" className="text-[#0e0f23]/40">&rarr;</span>
                    </button>
                  ))}
                </div>
                <div className="text-center text-xs text-[#0e0f23]/60">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setError('');
                      setSelectedGcipTenantId(presetTenant?.gcipTenantId ?? null);
                      setSelectedTenantName(null);
                    }}
                    className="font-medium text-[#20556f] underline hover:text-[#1a4658]"
                  >
                    Return to login
                  </button>
                </div>
              </div>
            )}

            {step === 'password' && (
              <form onSubmit={onSubmitPassword} className="flex w-full flex-col gap-4">
                <input
                  type="email"
                  id="username"
                  name="username"
                  autoComplete="username"
                  value={email}
                  readOnly
                  aria-hidden="true"
                  tabIndex={-1}
                  className="sr-only"
                />
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="h-10 w-full rounded-md border border-[#0e0f23]/15 bg-white px-3 text-sm text-[#0e0f23] focus:outline-none focus:ring-2 focus:ring-[#20556f]/50"
                />
                <SubmitButton busy={busy} label="Sign in" />
                <div className="flex items-center justify-between text-xs text-[#0e0f23]/60">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setPassword('');
                      setError('');
                      setSelectedGcipTenantId(presetTenant?.gcipTenantId ?? null);
                      setSelectedTenantName(null);
                    }}
                    className="font-medium text-[#20556f] underline hover:text-[#1a4658]"
                  >
                    Return to login
                  </button>
                  <Link href={forgotPasswordHref} className="font-medium text-[#20556f] underline hover:text-[#1a4658]">
                    Forgot password?
                  </Link>
                </div>
              </form>
            )}

            {step === 'enroll' && enrollInfo && (
              <form onSubmit={onSubmitEnroll} className="flex w-full flex-col gap-4">
                <p className="text-sm leading-relaxed text-[#0e0f23]/80">
                  Add this account to an authenticator app (Google Authenticator, Microsoft
                  Authenticator, 1Password, etc.) by scanning the QR code below — or enter the
                  setup key manually — then enter the 6-digit code it generates.
                </p>
                <div className="rounded-lg bg-white p-3 text-center">
                  <p className="text-xs text-[#0e0f23]/60">Setup key</p>
                  {enrollInfo.qrCodeUrl && (
                    <div className="my-3 flex justify-center">
                      <QRCodeSVG
                        value={enrollInfo.qrCodeUrl}
                        size={160}
                        level="M"
                        marginSize={2}
                        className="rounded-md bg-white p-2 shadow-sm"
                      />
                    </div>
                  )}
                  <code className="block break-all text-sm font-semibold tracking-wider text-[#0e0f23]">
                    {enrollInfo.secretKey}
                  </code>
                </div>
                <CodeInput value={code} onChange={setCode} />
                <SubmitButton busy={busy} label="Verify and enroll" />
                <div className="text-center text-xs text-[#0e0f23]/60">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setPassword('');
                      setCode('');
                      setEnrollInfo(null);
                      setError('');
                      setSelectedGcipTenantId(presetTenant?.gcipTenantId ?? null);
                      setSelectedTenantName(null);
                    }}
                    className="font-medium text-[#20556f] underline hover:text-[#1a4658]"
                  >
                    Return to login
                  </button>
                </div>
              </form>
            )}

            {step === 'challenge' && (
              <form onSubmit={onSubmitChallenge} className="flex w-full flex-col gap-4">
                <p className="text-sm leading-relaxed text-[#0e0f23]/80">
                  Enter the 6-digit code from your authenticator app.
                </p>
                <CodeInput value={code} onChange={setCode} />
                <SubmitButton busy={busy} label="Verify" />
                <div className="text-center text-xs text-[#0e0f23]/60">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setPassword('');
                      setCode('');
                      setError('');
                      setSelectedGcipTenantId(presetTenant?.gcipTenantId ?? null);
                      setSelectedTenantName(null);
                    }}
                    className="font-medium text-[#20556f] underline hover:text-[#1a4658]"
                  >
                    Return to login
                  </button>
                </div>
              </form>
            )}

            <div className="text-center text-xs text-[#0e0f23]/60">
              Trouble signing in?<br />
              <a href="mailto:support@foltrigg.ai?subject=Login%20issue" className="font-medium text-[#20556f] underline hover:text-[#1a4658]">
                Contact Foltrigg Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CodeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={6}
      required
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      placeholder="000000"
      className="h-12 w-full rounded-md border border-[#0e0f23]/15 bg-white px-3 text-center text-lg tracking-[0.5em] text-[#0e0f23] focus:outline-none focus:ring-2 focus:ring-[#20556f]/50"
    />
  );
}

function SubmitButton({ busy, label }: { busy: boolean; label: string }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="flex h-10 w-full items-center justify-center rounded-md bg-[#20556f] px-4 text-base font-medium text-[#f8fafa] transition-colors duration-200 hover:bg-[#1a4658] focus:outline-none focus:ring-2 focus:ring-[#20556f]/50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy ? (
        <svg className="h-5 w-5 animate-spin text-[#f8fafa]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        label
      )}
    </button>
  );
}
